/**
 * Loan Center detail-sheet helpers for safe numeric formatting and canonical comparison.
 *
 * These exist because Supabase Realtime delivers PostgreSQL `numeric` columns as strings,
 * while the RPC `search_encompass_loans` delivers them as numbers via `json_agg`. All
 * render and comparison code in the detail flow must tolerate either representation.
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

/** Safe numeric coercion. Returns null for null, undefined, '', NaN, Infinity. */
export function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a possibly-string numeric to fixed decimals.
 * Returns '-' on null, undefined, NaN, Infinity, or non-numeric strings.
 */
export function formatFixed(value: unknown, decimals: number): string {
  const n = toNumberOrNull(value);
  return n !== null ? n.toFixed(decimals) : '-';
}

/**
 * Format as percentage string (e.g. "6.750%").
 * Returns '-' on null, undefined, NaN, or non-numeric strings.
 */
export function formatPercent(value: unknown, decimals: number): string {
  const n = toNumberOrNull(value);
  return n !== null ? `${n.toFixed(decimals)}%` : '-';
}

/**
 * Canonical equality for override reconciliation and snapshot filtering.
 *
 * Rules (in order):
 * 1. null, undefined, and '' are all equivalent blanks.
 * 2. If both values coerce to the same finite number, they are equal
 *    (handles "103.425" === 103.425).
 * 3. If both values look like ISO dates (/^\d{4}-\d{2}-\d{2}/), compare
 *    the date portion before 'T' (handles "2026-03-15" === "2026-03-15T00:00:00").
 * 4. Otherwise, compare as strings literally.
 */
export function canonEqual(a: unknown, b: unknown): boolean {
  const aBlank = a === null || a === undefined || a === '';
  const bBlank = b === null || b === undefined || b === '';
  if (aBlank && bBlank) {
    return true;
  }
  if (aBlank || bBlank) {
    return false;
  }

  // Numeric equality
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) {
    return na === nb;
  }

  // ISO date normalization (only for date-like strings)
  const sa = String(a);
  const sb = String(b);
  if (ISO_DATE_RE.test(sa) && ISO_DATE_RE.test(sb)) {
    return sa.split('T')[0] === sb.split('T')[0];
  }

  // Literal string comparison
  return sa === sb;
}

/**
 * JSON-aware deep equality for structured payloads (objects/arrays).
 *
 * `canonEqual` is unsafe for objects — it does `String(v)` which yields
 * "[object Object]" for any object, so two structurally different payloads
 * compare equal. Use this comparator for `le_fees` and any other JSONB column
 * delivered as a nested structure.
 *
 * Rules (in order):
 * 1. null, undefined, and '' are all equivalent blanks.
 * 2. Strict equality (covers identical primitives or same reference).
 * 3. Stable-key JSON.stringify comparison (key-order independent for objects).
 *
 * NaN is intentionally unsupported (JSON.stringify yields "null" for NaN — same
 * as canonEqual's behavior).
 */
export function jsonStableEqual(a: unknown, b: unknown): boolean {
  const aBlank = a === null || a === undefined || a === '';
  const bBlank = b === null || b === undefined || b === '';
  if (aBlank && bBlank) {
    return true;
  }
  if (aBlank || bBlank) {
    return false;
  }
  if (a === b) {
    return true;
  }
  return JSON.stringify(a, sortedKeysReplacer) === JSON.stringify(b, sortedKeysReplacer);
}

function sortedKeysReplacer(_key: string, value: unknown): unknown {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).sort(([k1], [k2]) => k1.localeCompare(k2))
    );
  }
  return value;
}

/**
 * Total issued lender credit on the loan, broken into its two Encompass sources.
 *
 * - `nonSpecific`: Encompass field 4794 (Non-Specific Lender Credit). Editable.
 *   Writes from the pricing UI target this field only.
 * - `lenderPaidCC`: Encompass field LENPCC (Lender-Paid Closing Costs). Read-only;
 *   aggregated from line items on the loan.
 * - `total`: `nonSpecific + lenderPaidCC`. Additive, not alternatives. This is the
 *   true total applied to the borrower path for waterfall math.
 *
 * Canonical precedent: `BankerRecon.tsx:917-956` — "Total lender credit = Field
 * 4794 + LENPCC (additive, not alternatives)".
 *
 * LENPCC is stored in `le_fields` JSONB as a comma-formatted string today
 * (e.g. "21,793.00"); currency strings ("$21,793.00") also round-trip safely.
 */
export function lenderCreditBreakdown(loan: {
  trans_details_non_specific_lender_credit?: number | string | null;
  le_fields?: Record<string, string | number | null>;
}): { nonSpecific: number; lenderPaidCC: number; total: number } {
  const nonSpecific = toNumberOrNull(loan.trans_details_non_specific_lender_credit) ?? 0;
  const lenpccRaw = loan.le_fields?.['LENPCC'];
  const lenderPaidCC = lenpccRaw === null || lenpccRaw === undefined
    ? 0
    : (toNumberOrNull(String(lenpccRaw).replace(/[$,\s]/g, '')) ?? 0);
  return { nonSpecific, lenderPaidCC, total: nonSpecific + lenderPaidCC };
}
